export type StateStatus = 'not_started' | 'in_progress' | 'complete'

export interface RenewalEntry {
  year: number
  fee: number
  date: string
}

export interface ChargeItem {
  name: string
  amount: number
}

export interface User {
  id: string
  name: string
  nmlsId: string
}

export interface LicenseData {
  status: StateStatus
  cost: number
  charges: ChargeItem[]
  startDate: string | null
  expirationDate: string | null
  renewals: RenewalEntry[]
}

export interface MichiganLicenseData {
  steps: boolean[]
  stepCosts: number[]
  startDate: string | null
  expirationDate: string | null
  renewals: RenewalEntry[]
}

export interface StateComplianceData {
  renewalDate: string | null
  suretyBondAmount: number | null
  suretyBondHeldBy: string | null
  secretaryOfStateRenewalDate: string | null
  auditDate: string | null
}

export interface TrackerState {
  users: User[]
  licenses: Record<string, Record<string, LicenseData>>
  michiganLicenses: Record<string, MichiganLicenseData>
  stateCompliance: Record<string, StateComplianceData>
}

export const US_STATES = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' },
]

export const DEFAULT_MICHIGAN_COSTS = [30, 350, 0, 110, 175, 50]

export const MICHIGAN_STEPS = [
  'Sign up for an NMLS Number',
  'Complete 20 Hours of Training',
  'Complete Internal Licensing Training',
  'SAFE Exam Complete',
  'Fingerprints, Background Check, Credit Check, NMLS Filing',
  '2 Hours Michigan Education',
]

export function createDefaultLicense(): LicenseData {
  return {
    status: 'not_started',
    cost: 0,
    charges: [],
    startDate: null,
    expirationDate: null,
    renewals: [],
  }
}

export function createDefaultMichiganLicense(): MichiganLicenseData {
  return {
    steps: [false, false, false, false, false, false],
    stepCosts: [...DEFAULT_MICHIGAN_COSTS],
    startDate: null,
    expirationDate: null,
    renewals: [],
  }
}

export function createDefaultStateCompliance(): StateComplianceData {
  return {
    renewalDate: null,
    suretyBondAmount: null,
    suretyBondHeldBy: null,
    secretaryOfStateRenewalDate: null,
    auditDate: null,
  }
}

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9)
}

export const DEFAULT_OFFICERS: User[] = [
  { id: 'lo-01', name: 'Michael Scott', nmlsId: '1608022' },
  { id: 'lo-02', name: 'Dwight Schrute', nmlsId: '1608022' },
  { id: 'lo-03', name: 'Jim Halpert', nmlsId: '2460756' },
  { id: 'lo-04', name: 'Pam Beesly', nmlsId: '2445638' },
  { id: 'lo-05', name: 'Ryan Howard', nmlsId: '2117088' },
  { id: 'lo-06', name: 'Kelly Kapoor', nmlsId: '1622391' },
  { id: 'lo-07', name: 'Oscar Martinez', nmlsId: '1761286' },
  { id: 'lo-08', name: 'Angela Martin', nmlsId: '2423706' },
  { id: 'lo-09', name: 'Phyllis Vance', nmlsId: '2572991' },
  { id: 'lo-10', name: 'Stanley Hudson', nmlsId: '2117086' },
  { id: 'lo-11', name: 'Erin Hannon', nmlsId: '1886697' },
  { id: 'lo-12', name: 'Creed Bratton', nmlsId: '2689141' },
  { id: 'lo-13', name: 'Toby Flenderson', nmlsId: '1237711' },
]

export function getUserLicensedStateCount(
  userId: string,
  licenses: Record<string, Record<string, LicenseData>>,
  michiganLicenses: Record<string, MichiganLicenseData>,
): { licensed: number; inProgress: number } {
  let licensed = 0
  let inProgress = 0

  const userLicenses = licenses[userId] || {}
  for (const stateName of Object.keys(userLicenses)) {
    if (userLicenses[stateName].status === 'complete') licensed++
    else if (userLicenses[stateName].status === 'in_progress') inProgress++
  }

  const mi = michiganLicenses[userId]
  if (mi) {
    const allComplete = mi.steps.every(Boolean)
    const someComplete = mi.steps.some(Boolean)
    if (allComplete) licensed++
    else if (someComplete) inProgress++
  }

  return { licensed, inProgress }
}

export function getStateLicensedUserCount(
  stateName: string,
  users: User[],
  licenses: Record<string, Record<string, LicenseData>>,
  michiganLicenses: Record<string, MichiganLicenseData>,
): { licensed: number; inProgress: number; total: number } {
  let licensed = 0
  let inProgress = 0
  let total = 0

  if (stateName === 'Michigan') {
    for (const user of users) {
      const mi = michiganLicenses[user.id]
      if (mi) {
        const allComplete = mi.steps.every(Boolean)
        const someComplete = mi.steps.some(Boolean)
        if (allComplete) {
          licensed++
          total++
        } else if (someComplete) {
          inProgress++
          total++
        }
      }
    }
  } else {
    for (const user of users) {
      const lic = licenses[user.id]?.[stateName]
      if (lic) {
        if (lic.status === 'complete') {
          licensed++
          total++
        } else if (lic.status === 'in_progress') {
          inProgress++
          total++
        } else if (lic.status === 'not_started') {
          total++
        }
      }
    }
  }

  return { licensed, inProgress, total }
}
